import React from 'react';
import { RotateCcw, Wand2, Info } from 'lucide-react';
import { toast } from 'sonner';
import {
  ComposedChart, Area, Line, Bar, BarChart, XAxis, YAxis, CartesianGrid, ReferenceLine,
} from 'recharts';
import {
  ChartContainer, ChartTooltip, ChartTooltipContent, ChartLegend, ChartLegendContent,
} from '@/components/ui/chart';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Separator } from '@/components/ui/separator';
import { Badge } from '@/components/ui/badge';
import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { Tooltip, TooltipTrigger, TooltipContent } from '@/components/ui/tooltip';
import {
  Table, TableHeader, TableBody, TableRow, TableHead, TableCell,
} from '@/components/ui/table';
import {
  KpiCard, ChartCard, ExportMenu, ScreenHeader, AXIS_TICK,
} from '@/components/app/kit.jsx';
import { useOps } from '@/hooks/useOps.jsx';
import {
  DEFAULT_LEVERS, STRING_LEVERS, BOOLEAN_LEVERS, project, infraCostForHospitals,
  deriveAiCostPerConsult, sensitivity, aiCostBreakdown, effectiveAiCostPerConsult,
  subscriptionRevenuePerHospital, usesPerConsultRevenue, usesTieredRevenue,
  tieredMonthlyRevenuePerHospital, ipdMonthly, hardwarePerHospital,
  perHospitalAtScale, hardwareMarginPerHospital, hardwareSold, mobileDataPerHospital,
} from '@/lib/economics.js';
import { DEFAULT_PRICES } from '@/lib/default-prices.js';
import { totalINR } from '@/lib/aggregate.js';
import { fmtINR, fmtCompact, fmtNum } from '@/lib/format.js';
import { downloadCsv, downloadTxtReport } from '@/lib/export.js';

// Economics — the what-if unit-economics simulator. Levers → src/lib/economics.js
// (pure math): 12-month EBITDA P&L across OPD + IPD, CAC/LTV/payback, a DERIVED
// AI-cost-per-consult breakdown, a tiered "slabs + base" pricing model, discounted
// cash-flow (NPV/IRR), and the tiered "go server" infra step function. Every lever
// carries an in-place plain-language explanation (info tooltip). "From live data"
// fills the blended AI-cost OVERRIDE from observed usage.

// FX used to convert USD list rates to INR for the provider presets — calibrated so
// Deepgram's $0.0077/min lands at the ≈₹0.66/min figure quoted below (0.66 / 0.0077).
const USD_INR = 85.7;
const priceByKey = Object.fromEntries(DEFAULT_PRICES.map((p) => [p.priceKey, p]));
const r2 = (n) => Math.round(n * 100) / 100;
// Deepgram Nova-3 streaming STT: $0.0077/min → ≈₹0.66/min.
const deepgramPerMin = r2(priceByKey['deepgram:nova-3:audio-seconds'].amount * USD_INR);
// ElevenLabs ConvAI voice minutes: $0.10/min → ≈₹8.57 for ~1 min of TTS/consult.
const elevenPerConsult = r2(priceByKey['elevenlabs:convai:audio-seconds'].amount * USD_INR);
// gpt-5.4-mini as the consult LLM: ~6k input + 1.5k output tokens per consult at the
// list per-1M rates → a computed ₹/consult (an assumption on the token mix).
const gptLlmPerConsult = r2((
  (6000 / 1e6) * priceByKey['openai:gpt-5-4-mini:input-tokens'].amount
  + (1500 / 1e6) * priceByKey['openai:gpt-5-4-mini:output-tokens'].amount
) * USD_INR);

// Provider presets — LIST rates (see default-prices.js), converted to each lever's
// unit. Selecting one fills the numeric lever; it stays editable, and the select
// shows "Custom" once the value no longer matches any preset.
// Preset labels are kept SHORT (provider — ≈₹rate) so the SelectValue never overflows
// its trigger. Any token-mix / calibration detail lives in the `hint` → shown in the
// row's InfoDot, not the label.
const STT_PRESETS = [
  { label: 'Sarvam Saaras — ₹0.50', value: 0.50, hint: 'Sarvam Saaras streaming STT list rate ₹0.50 / minute.' },
  { label: `Deepgram Nova-3 — ≈₹${deepgramPerMin}`, value: deepgramPerMin, hint: `Deepgram Nova-3 streaming $0.0077/min → ≈₹${deepgramPerMin} / minute at ₹85.7/$.` },
  { label: 'Blend — ₹0.55', value: 0.55, hint: 'Blend of Sarvam ₹0.50 and Deepgram ₹0.66 → ₹0.55 / minute.' },
];
const TTS_PRESETS = [
  { label: 'Supertonic — ₹0', value: 0, hint: 'Supertonic runs on-device → ₹0 / consult.' },
  { label: 'Sarvam Bulbul — ≈₹3', value: 3, hint: 'Sarvam Bulbul cloud TTS ≈₹3 / consult.' },
  { label: `ElevenLabs — ≈₹${elevenPerConsult}`, value: elevenPerConsult, hint: `ElevenLabs ConvAI $0.10/min → ≈₹${elevenPerConsult} for ~1 min of TTS / consult.` },
];
const LLM_PRESETS = [
  { label: 'Gemma-on-Cerebras — ≈₹1.7', value: 1.7, hint: 'Gemma-on-Cerebras intake + drafting legs ≈₹1.7 / consult.' },
  { label: `gpt-5.4-mini — ≈₹${gptLlmPerConsult}`, value: gptLlmPerConsult, hint: `gpt-5.4-mini at ~6k input + 1.5k output tokens/consult on the list per-1M rates → ≈₹${gptLlmPerConsult} / consult (an assumption on the token mix).` },
];
const PRESET_INFO = 'These are public LIST rates (default-prices.js), converted to this lever’s unit — starting points, not your contract rates. USD rates use ≈₹85.7/$ (calibrated to Deepgram). Picking one fills the number; you can still edit it.';

// Plain-language explanation for every lever (shown via the info affordance).
const FIELD_INFO = {
  startingHospitals: 'Paying hospitals live at the start of the projection window. No acquisition cost is charged for these — they were landed before month 1.',
  hospitalsAddedPerMonth: 'New paying hospitals signed each month after month 1. Each one incurs the full CAC in the month it lands.',
  churnMonthlyPct: '% of paying hospitals that cancel each month; 2% ≈ lose 1 of 50 monthly; LTV = monthly margin ÷ churn.',
  consultsPerHospitalPerDay: 'OPD consults a typical hospital runs per working day. Drives per-consult AI cost and (in the hybrid/tiered models) usage revenue.',
  workingDaysPerMonth: 'Clinic days per month — consults/day × this = monthly consult volume per hospital.',
  sttMinutesPerConsult: 'Speech-to-text minutes billed per consult; default 12.5 = 2.5 kiosk intake + 10 in-consult.',
  sttPerMinuteINR: 'Blended speech-to-text rate: the average of Sarvam ₹0.50 and Deepgram ₹0.66 per minute.',
  ttsPerConsultINR: 'Text-to-speech ₹/consult; default 0 because Supertonic runs on-device (was ₹3 on Sarvam Bulbul).',
  llmPerConsultINR: 'LLM / inference ₹/consult across the Gemma-on-Cerebras intake and drafting legs.',
  messagingPerPatientINR: 'WhatsApp messaging ₹/patient; default 0.7 = 3–4 messages at ₹0.20 each (one patient per consult).',
  aiCostOverrideINR: 'One blended AI ₹/consult to use instead of the breakdown. The "AI cost from live data" button fills this from observed usage.',
  pricingModel: 'How hospitals pay: a flat monthly platform fee, a per-doctor seat fee, flat + per-consult usage (hybrid), or a base fee + graduated per-consult slabs (tiered).',
  subscriptionPerHospitalINR: 'Flat platform fee per hospital per month (used by the flat and hybrid models).',
  pricePerConsultINR: 'Usage fee charged per consult — applies only in the hybrid model.',
  doctorsPerHospital: 'Billable doctors per hospital — the per-seat model multiplies this by the seat price.',
  pricePerSeatINR: '₹ per doctor per month in the per-seat model.',
  tierBaseFeeINR: 'Tiered model: a fixed platform fee every hospital pays each month, on top of the graduated per-consult slabs. Guarantees a revenue floor per hospital.',
  slab1UpToConsults: 'Tiered model: the first slab covers consults from 0 up to this count, each priced at the slab-1 rate.',
  slab1INR: 'Tiered model: ₹ per consult inside slab 1 (the highest, small-volume rate).',
  slab2UpToConsults: 'Tiered model: the second slab covers consults from the slab-1 cap up to this count, at the slab-2 rate.',
  slab2INR: 'Tiered model: ₹ per consult inside slab 2 (the mid-volume rate).',
  slab3INR: 'Tiered model: ₹ per consult beyond the slab-2 cap — the marginal floor for high-volume hospitals.',
  setupFeePerHospitalINR: 'Tiered model: a one-time onboarding fee, booked as revenue in the month each NEW hospital lands. It offsets that month’s CAC and also enters the NPV/IRR cash flow.',
  discountRateAnnualPct: 'Annual discount rate for the net-present-value (NPV) of the monthly cash flows. The monthly rate used is (1 + this)^(1/12) − 1. 18% is a typical early-stage cost of capital — replace with yours.',
  ipdHospitalPct: '% of hospitals that also run in-patient care (IPD) — the rest are OPD-only.',
  ipdAdmissionsPerHospitalPerMonth: 'Average admissions per IPD hospital per month.',
  ipdAvgLengthOfStayDays: 'Average length of stay in days — admissions × this = bed-days.',
  ipdPricingMode: 'Charge IPD either per admission (a flat fee) or per bed-day.',
  ipdPricePerAdmissionINR: '₹ charged per IPD admission (per-admission mode).',
  ipdPricePerBedDayINR: '₹ charged per IPD bed-day (per-bed-day mode).',
  ipdAiCostPerBedDayINR: 'AI ₹/bed-day for ward-round speech-to-text + due-engine inference.',
  cacPerHospitalINR: 'Everything to land one hospital: sales visits, demos, onboarding, staff training days, first-month support.',
  kioskCostINR: 'What ONE kiosk costs US (tablet + stand + SIM) — our cost of goods, before any markup.',
  kioskSellPriceINR: 'What we CHARGE the hospital per kiosk. We buy the tablet at the per-kiosk cost and re-sell at this price: the margin is booked as a product sale in the month each hospital lands (hardware revenue − hardware COGS), so it has NO capex and NO amortization. Set 0 to give hardware free — then it becomes our capex, amortized below EBITDA.',
  kiosksPerHospital: 'Kiosks handed to each hospital — hospital hardware = this × per-kiosk cost.',
  hardwareAmortizationMonths: "Months to spread GIVEN-FREE kiosk hardware over — the depreciation shown below EBITDA, not inside it. Ignored when kiosks are sold at a price (then hardware is expensed as COGS at sale).",
  mobileDataPerSeatINR: 'Monthly SIM/data allowance per staff seat — ₹350 × doctors/hospital = a per-hospital serving cost inside COGS. It scales with hospitals (like AI cost), not a fixed overhead.',
  staffCostMonthlyINR: 'Total team payroll per month.',
  miscOpexMonthlyINR: 'Tools, travel, compliance and other monthly overhead.',
  baseInfraMonthlyINR: 'Seeded from the current real footprint (EC2 + Medplum ECS/RDS ≈ ₹30k/mo); capacity per server is an assumption — replace with observed numbers.',
  hospitalsPerServer: 'How many hospitals one server tier covers before another is added — an assumption to replace with observed numbers.',
  serverStepMonthlyINR: 'Cost of each additional dedicated server tier.',
};

// Small info affordance — registry Tooltip, keyboard-focusable, shows on focus too.
function InfoDot({ text }) {
  if (!text) return null;
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <button
          type="button"
          aria-label="What this lever means"
          className="inline-flex size-3.5 items-center justify-center rounded-full text-muted-foreground/70 transition-colors hover:text-foreground focus-visible:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        >
          <Info className="size-3.5" />
        </button>
      </TooltipTrigger>
      <TooltipContent className="max-w-[250px] text-[11.5px] font-normal leading-relaxed">{text}</TooltipContent>
    </Tooltip>
  );
}

// Instant-feedback chip: gross-margin % before → after + the M12 EBITDA delta from
// the single edit that just happened. cc-enter is transform/opacity only, ≤240ms,
// and honours prefers-reduced-motion (styles/shadcn.css).
function DeltaChip({ gmBefore, gmAfter, ebitdaBefore, ebitdaAfter }) {
  const gmDelta = gmAfter - gmBefore;
  const arrow = Math.abs(gmDelta) < 0.05 ? '▬' : (gmDelta > 0 ? '▲' : '▼');
  const gmGood = gmDelta >= 0;
  const ebDelta = ebitdaAfter - ebitdaBefore;
  const ebGood = ebDelta >= 0;
  return (
    <Badge
      variant="outline"
      className="cc-enter mt-0.5 h-5 w-fit gap-1 px-1.5 text-[10px] font-semibold tabular-nums"
    >
      <span className="text-muted-foreground">GM {gmBefore.toFixed(1)}%</span>
      <span className="text-muted-foreground">→</span>
      <span className={gmGood ? 'text-[hsl(var(--success))]' : 'text-[hsl(var(--allergy))]'}>{gmAfter.toFixed(1)}% {arrow}</span>
      <span className="text-muted-foreground/70">·</span>
      <span className={ebGood ? 'text-[hsl(var(--success))]' : 'text-[hsl(var(--allergy))]'}>
        EBITDA {ebDelta >= 0 ? '+' : '−'}{fmtCompact(Math.abs(ebDelta))}
      </span>
    </Badge>
  );
}

// One numeric lever field: label + info dot + number input + (optional) delta chip.
function LeverField({ k, label, step, levers, onChange, disabled, chip }) {
  return (
    <div className="flex flex-col gap-1">
      <Label htmlFor={`lv-${k}`} className="flex items-center gap-1 text-[11px] leading-tight text-muted-foreground">
        <span className="min-w-0">{label}</span>
        <InfoDot text={FIELD_INFO[k]} />
      </Label>
      <Input id={`lv-${k}`} type="number" step={step} min="0" disabled={disabled}
        className="h-8 tabular-nums text-[12.5px]"
        value={levers[k]} onChange={onChange(k)} />
      {chip}
    </div>
  );
}

// Provider-preset row: a label + a compact registry Select that fills a numeric
// lever from a named list rate. Shows "Custom" when the value matches no preset.
function PresetRow({ label, k, presets, levers, setField, disabled }) {
  const cur = presets.find((p) => Math.abs(Number(levers[k]) - p.value) < 0.005);
  // The matched preset's token-mix / calibration detail rides in the InfoDot, so the
  // label (and thus the SelectValue) stays short and never overflows the trigger.
  const info = cur && cur.hint ? `${cur.hint} ${PRESET_INFO}` : PRESET_INFO;
  return (
    <div className="flex items-center gap-2">
      <span className="w-24 flex-none text-[11px] text-muted-foreground">{label}</span>
      <Select
        value={cur ? String(cur.value) : 'custom'}
        onValueChange={(v) => { if (v !== 'custom') setField(k, Number(v)); }}
        disabled={disabled}
      >
        <SelectTrigger className="h-7 min-w-0 flex-1 text-[11px]"><SelectValue className="block truncate" placeholder="Preset" /></SelectTrigger>
        <SelectContent>
          {presets.map((p) => (
            <SelectItem key={p.label} value={String(p.value)} className="text-[11px]">{p.label}</SelectItem>
          ))}
          {!cur && <SelectItem value="custom" className="text-[11px] text-muted-foreground">Custom (edited)</SelectItem>}
        </SelectContent>
      </Select>
      <InfoDot text={info} />
    </div>
  );
}

// Seeded-defaults ledger — the standing answer to "how did we get these numbers".
const ASSUMPTIONS = [
  ['Consults 60 / hospital / day · 26 working days', 'assumption'],
  ['STT 12.5 min / consult (2.5 kiosk + 10 consult)', 'assumption'],
  ['STT ₹0.55 / min (Sarvam ₹0.50 + Deepgram ₹0.66 blend)', 'researched'],
  ['TTS ₹0 / consult (Supertonic on-device; was ₹3 on Bulbul)', 'real'],
  ['LLM ₹1.7 / consult (Gemma-on-Cerebras intake + drafting)', 'researched'],
  ['Messaging ₹0.7 / patient (3–4 WhatsApp @ ₹0.20)', 'researched'],
  ['Subscription ₹8,000 / hospital / mo', 'assumption'],
  ['Price ₹12 / consult (hybrid) · per-seat ₹1,500 × 8 doctors', 'assumption'],
  ['Tiered: ₹3,000 base + slabs ₹15 / ₹10 / ₹6 · ₹50,000 setup', 'assumption'],
  ['IPD: 50% of hospitals · 40 admissions/mo · 3-day stay', 'assumption'],
  ['IPD price ₹150 / admission (or ₹50 / bed-day)', 'assumption'],
  ['IPD AI ₹2 / bed-day (ward-round STT + due engine)', 'assumption'],
  ['Churn 2% / month', 'assumption'],
  ['CAC ₹60,000 / hospital (sales + onboarding + training)', 'assumption'],
  ['Kiosk ₹25,000 one-time cost × 1 / hospital', 'researched'],
  ['Hardware sold ₹70,000 / kiosk → ₹45,000 margin (0 = given free, amortized 24 mo)', 'assumption'],
  ['Mobile data ₹350 / seat / mo × doctors = per-hospital serving cost', 'assumption'],
  ['Discount rate 18% / yr for NPV · IRR only when cash flow turns', 'assumption'],
  ['Infra ₹30,000 / mo baseline (EC2 + Medplum ECS/RDS)', 'real'],
  ['Capacity 10 hospitals / server · +₹22,000 / extra server', 'assumption'],
  ['Staff ₹3,50,000 / mo · misc opex ₹60,000 / mo', 'real'],
];
const STATUS_META = {
  real: { label: 'real footprint', cls: 'text-[hsl(var(--success))] border-[hsl(var(--success))]/40' },
  researched: { label: 'researched rate', cls: 'text-muted-foreground border-border' },
  assumption: { label: 'assumption — replace', cls: 'text-[hsl(var(--ai-foreground))] border-[hsl(var(--ai-border))]' },
};

const LEVERS_STORAGE_KEY = 'healther-ops:economics-levers:v4';
const LEVERS_STORAGE_KEY_V3 = 'healther-ops:economics-levers:v3';
const LEVERS_STORAGE_KEY_V2 = 'healther-ops:economics-levers:v2';
const LEVERS_STORAGE_KEY_V1 = 'healther-ops:economics-levers:v1';
const UI_STORAGE_KEY = 'healther-ops:economics-ui:v4';
const UI_STORAGE_KEY_V3 = 'healther-ops:economics-ui:v3';

// Chart series that live behind toggles — EVERY line now has its own toggle. Defaults
// on: the four P&L lines + AI cost total; off: infra and the STT/TTS component lines.
const VALID_SERIES = ['revenue', 'cogs', 'opex', 'ebitda', 'aiTotal', 'infra', 'sttCost', 'ttsCost'];
const DEFAULT_SERIES = ['revenue', 'cogs', 'opex', 'ebitda', 'aiTotal'];
// Human labels for the per-series toggle chips (order = display order).
const SERIES_TOGGLES = [
  ['revenue', 'Revenue'], ['cogs', 'COGS'], ['opex', 'Opex'], ['ebitda', 'EBITDA'],
  ['aiTotal', 'AI cost'], ['infra', 'Infra'], ['sttCost', 'STT'], ['ttsCost', 'TTS'],
];
// Projection-horizon presets for the chart-header Select (custom value via the input).
const HORIZON_PRESETS = [12, 18, 24, 36];

// Coerce a stored/partial object onto DEFAULT_LEVERS — never crash on stale shapes.
// Booleans, the two string levers, and numbers each sanitize differently; anything
// unknown or non-finite falls back to its default. New v3 lever keys absent from an
// older (v2/v1) blob simply keep their DEFAULT_LEVERS value.
function sanitizeStored(obj) {
  const out = { ...DEFAULT_LEVERS };
  if (!obj || typeof obj !== 'object') return out;
  for (const [k, v] of Object.entries(obj)) {
    if (!(k in DEFAULT_LEVERS)) continue;
    if (BOOLEAN_LEVERS.includes(k)) out[k] = Boolean(v);
    else if (STRING_LEVERS.includes(k)) { if (typeof v === 'string' && v) out[k] = v; }
    else if (v !== '' && v !== null && Number.isFinite(Number(v))) out[k] = Number(v);
  }
  return out;
}

// Migrate a v1 lever blob: keep every field name that survived, and map the old
// per-hospital hardware cost onto the new per-kiosk cost.
function migrateV1(v1) {
  const out = {};
  if (!v1 || typeof v1 !== 'object') return out;
  for (const [k, v] of Object.entries(v1)) {
    if (k in DEFAULT_LEVERS) out[k] = v;
  }
  if (v1.hardwareCostPerHospitalINR != null && out.kioskCostINR == null) {
    out.kioskCostINR = v1.hardwareCostPerHospitalINR;
  }
  return out;
}

// Load levers, newest key first. v3→v4, v2→v4 and v1→v4 all flow through
// sanitizeStored, which fills the new v4 levers (kioskSellPriceINR, mobileDataPerSeatINR)
// with defaults while carrying every live-tweaked value the user had (e.g.
// consultsPerHospitalPerDay, subscriptionPerHospitalINR) — owner tweaks survive.
function loadStoredLevers() {
  try {
    const rawV4 = localStorage.getItem(LEVERS_STORAGE_KEY);
    if (rawV4) return sanitizeStored(JSON.parse(rawV4));
    const rawV3 = localStorage.getItem(LEVERS_STORAGE_KEY_V3);
    if (rawV3) return sanitizeStored(JSON.parse(rawV3));
    const rawV2 = localStorage.getItem(LEVERS_STORAGE_KEY_V2);
    if (rawV2) return sanitizeStored(JSON.parse(rawV2));
    const rawV1 = localStorage.getItem(LEVERS_STORAGE_KEY_V1);
    if (rawV1) return sanitizeStored(migrateV1(JSON.parse(rawV1)));
    return { ...DEFAULT_LEVERS };
  } catch { return { ...DEFAULT_LEVERS }; }
}

function loadUiState() {
  try {
    const raw = localStorage.getItem(UI_STORAGE_KEY);
    if (raw) {
      const o = JSON.parse(raw);
      const series = Array.isArray(o.series) ? o.series.filter((s) => VALID_SERIES.includes(s)) : [...DEFAULT_SERIES];
      return { series };
    }
    // Migrate v3 → v4: in v3 the four P&L lines were ALWAYS drawn and only aiTotal/
    // stt/tts were toggled. Seed the four core lines on and carry across whatever
    // component toggles the user had, so the chart looks the same after the bump.
    const rawV3 = localStorage.getItem(UI_STORAGE_KEY_V3);
    if (rawV3) {
      const o = JSON.parse(rawV3);
      const carried = Array.isArray(o.series)
        ? o.series.filter((s) => ['aiTotal', 'sttCost', 'ttsCost'].includes(s)) : [];
      return { series: Array.from(new Set(['revenue', 'cogs', 'opex', 'ebitda', ...carried])) };
    }
    return { series: [...DEFAULT_SERIES] };
  } catch { return { series: [...DEFAULT_SERIES] }; }
}

const fmtPct1 = (n) => `${Number(n || 0).toFixed(1)}%`;

// Indian compact money for KPI values — ₹1.2Cr / ₹56.9L — so big figures never
// clip the card. Exact values stay available in subtitles and the P&L table.
function fmtINRc(v) {
  const n = Number(v || 0);
  if (!Number.isFinite(n)) return '—';
  const sign = n < 0 ? '−' : '';
  const a = Math.abs(n);
  if (a >= 1e7) return `${sign}₹${(a / 1e7).toLocaleString('en-IN', { maximumFractionDigits: a >= 1e8 ? 0 : 2 })}Cr`;
  if (a >= 1e5) return `${sign}₹${(a / 1e5).toLocaleString('en-IN', { maximumFractionDigits: a >= 1e6 ? 1 : 2 })}L`;
  return fmtINR(n);
}

export default function Economics({ range }) {
  const ops = useOps();
  const [levers, setLevers] = React.useState(loadStoredLevers);
  const [series, setSeries] = React.useState(() => loadUiState().series);
  const [edit, setEdit] = React.useState(null); // { key, seq, gmBefore, ebitdaBefore }
  const seqRef = React.useRef(0);

  React.useEffect(() => {
    try { localStorage.setItem(LEVERS_STORAGE_KEY, JSON.stringify(levers)); } catch { /* quota — non-fatal */ }
  }, [levers]);
  React.useEffect(() => {
    try { localStorage.setItem(UI_STORAGE_KEY, JSON.stringify({ series })); } catch { /* quota — non-fatal */ }
  }, [series]);

  // Sanitize for the MATH: empty/NaN numeric fields fall back to defaults, strings
  // and booleans pass through (inputs keep showing what the user typed).
  const L = React.useMemo(() => {
    const out = { ...DEFAULT_LEVERS };
    for (const [k, v] of Object.entries(levers)) {
      if (!(k in DEFAULT_LEVERS)) continue;
      if (BOOLEAN_LEVERS.includes(k)) out[k] = Boolean(v);
      else if (STRING_LEVERS.includes(k)) { if (typeof v === 'string' && v) out[k] = v; }
      else if (v !== '' && Number.isFinite(Number(v))) out[k] = Number(v);
    }
    // Horizon is an integer clamped to 3..60 months — the loop count for project().
    out.months = Math.min(60, Math.max(3, Math.round(out.months || DEFAULT_LEVERS.months)));
    return out;
  }, [levers]);
  const N = L.months; // projection horizon in months — every "M12" label is now M{N}

  const { months, summary } = React.useMemo(() => project(L), [L]);
  const sens = React.useMemo(() => sensitivity(L), [L]);
  const aiParts = React.useMemo(() => aiCostBreakdown(L), [L]);
  const aiPerConsult = effectiveAiCostPerConsult(L);
  const overrideOn = !!L.aiCostOverrideEnabled;

  // Keep the latest projection outputs in a ref so an edit can snapshot the "before"
  // gross margin + M12 EBITDA the instant it fires, then compare to the new outputs.
  const summaryRef = React.useRef(summary);
  React.useEffect(() => { summaryRef.current = summary; }, [summary]);

  const captureBefore = (k) => {
    const b = summaryRef.current;
    seqRef.current += 1;
    setEdit({ key: k, seq: seqRef.current, gmBefore: b.grossMarginPctM12, ebitdaBefore: b.endingMonthlyEbitda });
  };
  const set = (k) => (e) => {
    const v = e.target.value;
    captureBefore(k);
    setLevers((prev) => ({ ...prev, [k]: v === '' ? '' : Number(v) }));
  };
  const setField = (k, value) => { captureBefore(k); setLevers((prev) => ({ ...prev, [k]: value })); };
  // Horizon setter (chart-header Select) — clean integer, clamped to the 3..60 window.
  const setMonths = (v) => {
    const n = Math.round(Number(v));
    if (!Number.isFinite(n)) return;
    setField('months', Math.min(60, Math.max(3, n)));
  };

  // Delta chip for the just-edited field (numeric levers only). Compares the snapshot
  // taken at edit time to the recomputed M12 outputs.
  const chipFor = (k) => ((edit && edit.key === k) ? (
    <DeltaChip
      key={edit.seq}
      gmBefore={edit.gmBefore}
      gmAfter={summary.grossMarginPctM12}
      ebitdaBefore={edit.ebitdaBefore}
      ebitdaAfter={summary.endingMonthlyEbitda}
    />
  ) : null);

  // Per-unit economics (per average hospital / consult / IPD admission).
  const opdConsultsPerHospital = L.consultsPerHospitalPerDay * L.workingDaysPerMonth;
  const tieredOn = usesTieredRevenue(L);
  // OPD net per consult: in the tiered model the marginal price is the slab the
  // hospital's volume lands in; otherwise it's the flat per-consult price (hybrid).
  const marginalConsultPrice = tieredOn
    ? (opdConsultsPerHospital > L.slab2UpToConsults ? L.slab3INR
      : opdConsultsPerHospital > L.slab1UpToConsults ? L.slab2INR : L.slab1INR)
    : (usesPerConsultRevenue(L) ? L.pricePerConsultINR : 0);
  const opdNetPerConsult = marginalConsultPrice - aiPerConsult;
  const ipdRevPerAdmission = L.ipdPricingMode === 'per-bed-day'
    ? L.ipdAvgLengthOfStayDays * L.ipdPricePerBedDayINR
    : L.ipdPricePerAdmissionINR;
  const ipdAiPerAdmission = L.ipdAvgLengthOfStayDays * L.ipdAiCostPerBedDayINR;
  const ipdNetPerAdmission = ipdRevPerAdmission - ipdAiPerAdmission;

  // Derive blended AI cost/consult from LIVE observed data → fills the OVERRIDE.
  const deriveFromLive = () => {
    if (ops.loading || ops.error || !ops.enriched) { toast.error('Ops data not loaded yet.'); return; }
    const aiSpend = totalINR(ops.enriched.filter((r) => r.source === 'metered')).inr;
    const consultRuns = ops.telemetry.filter((t) => ['llm', 'harness', 'agent-turn', 'realtime'].includes(t.kind)).length;
    const derived = deriveAiCostPerConsult(aiSpend, consultRuns);
    if (derived === null) { toast.info('Not enough observed usage in this range to derive a per-consult cost.'); return; }
    const rounded = Math.round(derived * 100) / 100;
    setLevers((prev) => ({ ...prev, aiCostOverrideINR: rounded, aiCostOverrideEnabled: true }));
    toast.success(`Derived ₹${rounded} per consult from ${fmtNum(consultRuns)} observed AI runs (${range.from} → ${range.to}) — filled the blended override.`);
  };

  const resetAll = () => { setLevers({ ...DEFAULT_LEVERS }); setSeries([...DEFAULT_SERIES]); setEdit(null); };

  const chartRows = months.map((m) => ({
    label: `M${m.month}`,
    revenue: Math.round(m.revenue),
    cogs: Math.round(m.cogs),
    opex: Math.round(m.opex),
    ebitda: Math.round(m.ebitda),
    aiTotal: Math.round(m.aiCost),
    infra: Math.round(m.infra),
    // STT/TTS component lines from the per-consult breakdown × the month's consults.
    sttCost: Math.round(m.consults * aiParts.stt),
    ttsCost: Math.round(m.consults * aiParts.tts),
  }));

  // Per-hospital economics vs scale — h = 1 .. max(30, 1.5× ending). Shows fixed cost
  // amortizing away as hospitals grow (variable serving cost stays flat).
  const scaleMax = Math.max(30, Math.ceil(summary.endingHospitals * 1.5));
  const scaleRows = React.useMemo(() => perHospitalAtScale(L, scaleMax).map((r) => ({
    h: r.h,
    revenuePerHospital: Math.round(r.revenuePerHospital),
    totalCostPerHospital: Math.round(r.totalCostPerHospital),
    marginPerHospital: Math.round(r.marginPerHospital),
    fixedShare: Math.round(r.fixedShare),
  })), [L, scaleMax]);

  // Infra step-function preview (0 → ~1.4× ending hospitals).
  const maxH = Math.max(12, Math.ceil(summary.endingHospitals * 1.4));
  const stepRows = Array.from({ length: maxH + 1 }, (_, h) => ({
    h, infra: infraCostForHospitals(h, L),
  }));

  // Hardware sold-at-a-margin readout for the per-unit strip.
  const hwSold = hardwareSold(L);
  const hwMarginPerHospital = hardwareMarginPerHospital(L);

  const irrText = summary.irrAnnualPct === null ? 'n/a' : `${summary.irrAnnualPct.toFixed(0)}%`;
  const irrSub = summary.irrAnnualPct === null ? 'no upfront investment in window' : 'annualized, from monthly cash flow';

  const csv = () => downloadCsv(
    'ops-economics-projection.csv',
    [
      { key: 'month', label: 'Month' }, { key: 'hospitals', label: 'Hospitals' },
      { key: 'consults', label: 'Consults' },
      { key: 'revenue', label: 'Revenue INR' },
      { key: 'revenueOPD', label: 'Revenue OPD INR' }, { key: 'revenueIPD', label: 'Revenue IPD INR' },
      { key: 'revenueSetup', label: 'Setup fee INR' },
      { key: 'hardwareRevenue', label: 'Hardware revenue INR' },
      { key: 'hardwareCogs', label: 'Hardware COGS INR' },
      { key: 'aiCost', label: 'AI cost INR' },
      { key: 'aiCostOPD', label: 'AI cost OPD INR' }, { key: 'aiCostIPD', label: 'AI cost IPD INR' },
      { key: 'aiCostPerConsult', label: 'AI cost / consult INR' },
      { key: 'mobileDataCost', label: 'Mobile data INR' },
      { key: 'ipdAdmissions', label: 'IPD admissions' }, { key: 'ipdBedDays', label: 'IPD bed-days' },
      { key: 'infra', label: 'Infra INR' },
      { key: 'grossMargin', label: 'Gross margin INR' }, { key: 'opex', label: 'Opex INR' },
      { key: 'ebitda', label: 'EBITDA INR' },
      { key: 'hardwareAmortization', label: 'Hardware amortization INR' },
      { key: 'ebitAfterHardware', label: 'EBIT after hardware INR' },
      { key: 'capex', label: 'Kiosk capex INR' }, { key: 'cashFlow', label: 'Cash flow INR' },
      { key: 'cumulativeEbitda', label: 'Cumulative EBITDA INR' },
    ],
    months.map((m) => ({
      ...m,
      hospitals: m.hospitals.toFixed(1),
      consults: Math.round(m.consults),
      revenue: Math.round(m.revenue),
      revenueOPD: Math.round(m.revenueOPD), revenueIPD: Math.round(m.revenueIPD),
      revenueSetup: Math.round(m.revenueSetup),
      hardwareRevenue: Math.round(m.hardwareRevenue), hardwareCogs: Math.round(m.hardwareCogs),
      aiCost: Math.round(m.aiCost),
      aiCostOPD: Math.round(m.aiCostOPD), aiCostIPD: Math.round(m.aiCostIPD),
      aiCostPerConsult: m.aiCostPerConsult.toFixed(2),
      mobileDataCost: Math.round(m.mobileDataCost),
      ipdAdmissions: Math.round(m.ipdAdmissions), ipdBedDays: Math.round(m.ipdBedDays),
      infra: Math.round(m.infra),
      grossMargin: Math.round(m.grossMargin), opex: Math.round(m.opex),
      ebitda: Math.round(m.ebitda),
      hardwareAmortization: Math.round(m.hardwareAmortization),
      ebitAfterHardware: Math.round(m.ebitAfterHardware),
      capex: Math.round(m.capex), cashFlow: Math.round(m.cashFlow),
      cumulativeEbitda: Math.round(m.cumulativeEbitda),
    })),
  );
  const pricingLabel = {
    flat: 'flat ₹/hospital/mo',
    'per-seat': `per-seat ₹${L.pricePerSeatINR}/doctor × ${L.doctorsPerHospital}`,
    hybrid: 'flat + per-consult (hybrid)',
    tiered: `tiered — ₹${L.tierBaseFeeINR} base + slabs ₹${L.slab1INR}/₹${L.slab2INR}/₹${L.slab3INR} + ₹${L.setupFeePerHospitalINR} setup`,
  }[L.pricingModel] || L.pricingModel;
  const txt = () => downloadTxtReport('ops-economics-projection.txt', {
    title: 'Healther Ops — Unit economics (what-if)',
    meta: [
      `Levers: ${L.startingHospitals} hospitals +${L.hospitalsAddedPerMonth}/mo, churn ${L.churnMonthlyPct}%/mo`,
      `Pricing model: ${pricingLabel} · subscription ₹${L.subscriptionPerHospitalINR}/mo`,
      `AI cost/consult: ₹${aiPerConsult.toFixed(2)} ${overrideOn ? '(blended override)' : `(derived: STT ₹${aiParts.stt.toFixed(2)} + TTS ₹${aiParts.tts.toFixed(2)} + LLM ₹${aiParts.llm.toFixed(2)} + msg ₹${aiParts.messaging.toFixed(2)})`}`,
      `IPD: ${L.ipdHospitalPct}% of hospitals · ${L.ipdAdmissionsPerHospitalPerMonth} admissions/mo · ${L.ipdAvgLengthOfStayDays}-day stay · ${L.ipdPricingMode === 'per-bed-day' ? `₹${L.ipdPricePerBedDayINR}/bed-day` : `₹${L.ipdPricePerAdmissionINR}/admission`} · AI ₹${L.ipdAiCostPerBedDayINR}/bed-day`,
      `Infra: ₹${L.baseInfraMonthlyINR} base + ₹${L.serverStepMonthlyINR} per extra server (1 per ${L.hospitalsPerServer} hospitals)`,
      hwSold
        ? `Hardware: SOLD ₹${L.kioskSellPriceINR}/kiosk (cost ₹${L.kioskCostINR}) × ${L.kiosksPerHospital}/hospital → ₹${fmtNum(hwMarginPerHospital)} margin/hospital, ${fmtINR(summary.totalHardwareRevenue)} hardware revenue over ${N} mo`
        : `Hardware: GIVEN FREE ₹${L.kioskCostINR}/kiosk × ${L.kiosksPerHospital}/hospital, amortized ${L.hardwareAmortizationMonths} mo`,
      `Mobile data: ₹${L.mobileDataPerSeatINR}/seat/mo × ${L.doctorsPerHospital} doctors = ₹${fmtNum(mobileDataPerHospital(L))}/hospital/mo (${fmtINR(summary.totalMobileData)} over ${N} mo)`,
      '',
      `${N}-month totals: revenue ${fmtINR(summary.totalRevenue)} · EBITDA (${N}-mo total) ${fmtINR(summary.totalEbitda)}`,
      `Run-rate (M${N}): revenue ${fmtINR(summary.endingMonthlyRevenue)}/mo · EBITDA ${fmtINR(summary.endingMonthlyEbitda)}/mo`,
      `Margins (M${N}): gross ${fmtPct1(summary.grossMarginPctM12)} · EBITDA ${fmtPct1(summary.ebitdaMarginPctM12)} · after-amortization ≈EBIT ${fmtPct1(summary.ebitMarginPctM12)}`,
      `NPV @ ${summary.discountRateAnnualPct}%/yr: ${fmtINR(summary.npvINR)} · IRR (annualized): ${summary.irrAnnualPct === null ? 'n/a — no upfront investment in window' : summary.irrAnnualPct.toFixed(1) + '%'}`,
      `LTV: ${summary.ltv !== null ? fmtINR(summary.ltv) : 'unbounded (0 churn)'} · CAC: ${fmtINR(summary.cac)} · LTV:CAC ${summary.ltvToCac !== null ? summary.ltvToCac.toFixed(1) : '—'}`,
      `Payback: ${summary.paybackMonths !== null ? summary.paybackMonths.toFixed(1) + ' months' : '—'}`,
      `Break-even month: ${summary.breakEvenMonth ?? 'not within horizon'}`,
    ],
    sections: [{
      heading: `${N}-month P&L (INR)`,
      columns: [
        { key: 'month', label: 'M' }, { key: 'hospitals', label: 'Hosp' },
        { key: 'revenue', label: 'Revenue' }, { key: 'revOPD', label: 'Rev OPD' },
        { key: 'revIPD', label: 'Rev IPD' }, { key: 'cogs', label: 'COGS' },
        { key: 'opex', label: 'Opex' }, { key: 'ebitda', label: 'EBITDA' },
        { key: 'cashFlow', label: 'Cash flow' },
      ],
      rows: months.map((m) => ({
        month: m.month, hospitals: m.hospitals.toFixed(1),
        revenue: Math.round(m.revenue), revOPD: Math.round(m.revenueOPD), revIPD: Math.round(m.revenueIPD),
        cogs: Math.round(m.cogs), opex: Math.round(m.opex), ebitda: Math.round(m.ebitda),
        cashFlow: Math.round(m.cashFlow),
      })),
    }],
  });

  return (
    <div className="p-6 cc-enter">
      <ScreenHeader title="Economics" subtitle={`Simulator — hypothetical · pure math, INR, ${N}-month horizon across OPD + IPD`}>
        <Button variant="outline" size="sm" onClick={deriveFromLive}>
          <Wand2 className="size-3.5" /> AI cost from live data
        </Button>
        <Button variant="outline" size="sm" onClick={resetAll}>
          <RotateCcw className="size-3.5" /> Reset
        </Button>
        <ExportMenu onCsv={csv} onTxt={txt} />
      </ScreenHeader>

      {/* KPI row — spans full width at the top */}
      <div className="mb-4 flex flex-wrap gap-3">
        <KpiCard label="LTV" value={summary.ltv !== null ? fmtINRc(summary.ltv) : '∞'}
          sub={`margin ${fmtINRc(summary.marginPerHospitalMonthly)}/hospital/mo`} />
        <KpiCard label="LTV : CAC"
          tone={summary.ltvToCac !== null && summary.ltvToCac < 3 ? 'text-[hsl(var(--ai-foreground))]' : 'text-[hsl(var(--success))]'}
          value={summary.ltvToCac !== null ? `${summary.ltvToCac.toFixed(1)}×` : '∞'}
          sub={`CAC ${fmtINRc(summary.cac)}`} />
        <KpiCard label="Payback (CAC + hardware)" value={summary.paybackMonths !== null ? `${summary.paybackMonths.toFixed(1)} mo` : '—'}
          sub={`recovers ${fmtINRc(summary.cac + summary.hardwarePerHospital)} landed cost`} />
        <KpiCard label="AI cost / consult"
          tone={overrideOn ? 'text-[hsl(var(--ai-foreground))]' : 'text-foreground'}
          value={fmtINR(aiPerConsult)}
          sub={overrideOn ? 'blended override' : 'derived from sub-levers'} />
        <KpiCard label={`EBITDA (M${N} run-rate)`}
          tone={summary.endingMonthlyEbitda >= 0 ? 'text-[hsl(var(--success))]' : 'text-[hsl(var(--allergy))]'}
          value={fmtINRc(summary.endingMonthlyEbitda)}
          sub={summary.breakEvenMonth ? `break-even in month ${summary.breakEvenMonth}` : 'not break-even in horizon'} />
        <KpiCard label={`EBITDA (${N}-mo total)`}
          tone={summary.totalEbitda >= 0 ? 'text-[hsl(var(--success))]' : 'text-[hsl(var(--allergy))]'}
          value={fmtINRc(summary.totalEbitda)}
          sub={`on ${fmtINRc(summary.totalRevenue)} total revenue`} />
        <KpiCard label={`NPV @ ${summary.discountRateAnnualPct}%/yr`}
          tone={summary.npvINR >= 0 ? 'text-[hsl(var(--success))]' : 'text-[hsl(var(--allergy))]'}
          value={fmtINRc(summary.npvINR)}
          sub={`discounted ${N}-mo cash flow`} />
        <KpiCard label="IRR (annualized)"
          tone={summary.irrAnnualPct === null ? 'text-muted-foreground' : 'text-[hsl(var(--success))]'}
          value={irrText} sub={irrSub} />
        <KpiCard label={`Hospitals @ M${N}`} value={summary.endingHospitals.toFixed(0)}
          sub={`${fmtINRc(summary.endingMonthlyRevenue)} monthly revenue`} />
      </div>

      {/* Per-unit economics + M12 margins — one line each, full width */}
      <Card className="mb-4 gap-0 py-0">
        <CardContent className="flex flex-wrap items-center gap-x-6 gap-y-2 px-4 py-3 text-[12px]">
          <span className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">Per-unit economics</span>
          <span className="text-muted-foreground">OPD net / consult
            <span className={`ml-1.5 font-semibold tabular-nums ${opdNetPerConsult >= 0 ? 'text-[hsl(var(--success))]' : 'text-[hsl(var(--allergy))]'}`}>{fmtINR(opdNetPerConsult)}</span>
            {tieredOn && <span className="ml-1 text-[11px] text-muted-foreground/70">(marginal slab)</span>}
          </span>
          <span className="text-muted-foreground">IPD net / admission
            <span className={`ml-1.5 font-semibold tabular-nums ${ipdNetPerAdmission >= 0 ? 'text-[hsl(var(--success))]' : 'text-[hsl(var(--allergy))]'}`}>{fmtINR(ipdNetPerAdmission)}</span>
            <span className="ml-1 text-[11px] text-muted-foreground/70">(rev {fmtINR(ipdRevPerAdmission)} − AI {fmtINR(ipdAiPerAdmission)})</span>
          </span>
          <span className="text-muted-foreground">Margin / hospital / mo
            <span className="ml-1.5 font-semibold tabular-nums text-foreground">{fmtINR(summary.marginPerHospitalMonthly)}</span>
          </span>
          <span className="text-muted-foreground">Hardware margin / hospital
            <span className={`ml-1.5 font-semibold tabular-nums ${hwSold ? (hwMarginPerHospital >= 0 ? 'text-[hsl(var(--success))]' : 'text-[hsl(var(--allergy))]') : 'text-foreground'}`}>{hwSold ? fmtINR(hwMarginPerHospital) : '—'}</span>
            <span className="ml-1 text-[11px] text-muted-foreground/70">{hwSold ? `(sold ${fmtINR(L.kioskSellPriceINR)} − cost ${fmtINR(L.kioskCostINR)}, one-time)` : '(given free)'}</span>
          </span>
          <Separator orientation="vertical" className="!h-4" />
          <span className="text-muted-foreground">Gross margin (M{N})
            <span className="ml-1.5 font-semibold tabular-nums text-foreground">{fmtPct1(summary.grossMarginPctM12)}</span>
          </span>
          <span className="text-muted-foreground">EBITDA margin (M{N})
            <span className={`ml-1.5 font-semibold tabular-nums ${summary.ebitdaMarginPctM12 >= 0 ? 'text-[hsl(var(--success))]' : 'text-[hsl(var(--allergy))]'}`}>{fmtPct1(summary.ebitdaMarginPctM12)}</span>
          </span>
          <span className="text-muted-foreground">≈EBIT margin (M{N})
            <span className={`ml-1.5 font-semibold tabular-nums ${summary.ebitMarginPctM12 >= 0 ? 'text-[hsl(var(--success))]' : 'text-[hsl(var(--allergy))]'}`}>{fmtPct1(summary.ebitMarginPctM12)}</span>
            <span className="ml-1 text-[11px] text-muted-foreground/70">(after hardware amortization)</span>
          </span>
        </CardContent>
      </Card>

      {/* What moves EBITDA — ±20% one-at-a-time on the biggest levers (kept up top,
          right under per-unit economics, per owner request) */}
      <div className="mb-4">
        <div className="mb-1.5 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">What moves EBITDA — ±20% on one lever at a time</div>
        <div className="flex flex-wrap gap-2">
          {sens.map((s) => (
            <div key={s.lever} className="flex items-center gap-2.5 rounded-lg border bg-card px-3 py-1.5 text-[12px]">
              <span className="font-medium text-foreground">{s.label} ±{s.pct}%</span>
              <span className="tabular-nums text-[hsl(var(--allergy))]">{fmtCompact(Math.min(s.downEbitda, s.upEbitda))}</span>
              <span className="text-muted-foreground/60">←</span>
              <span className="tabular-nums text-muted-foreground">{fmtCompact(s.baseEbitda)}</span>
              <span className="text-muted-foreground/60">→</span>
              <span className="tabular-nums text-[hsl(var(--success))]">{fmtCompact(Math.max(s.downEbitda, s.upEbitda))}</span>
              <span className="text-[10.5px] text-muted-foreground/70">M{N} EBITDA</span>
            </div>
          ))}
        </div>
      </div>

      {/* Projection chart — full width, with the levers DIRECTLY beneath so a change
          and its effect share the screen (owner request: real-time cause → effect) */}
      <ChartCard
        title={`${N}-month projection`}
        subtitle="Revenue vs COGS vs opex, EBITDA line (INR/month) · toggle any series · set the horizon"
        action={(
          <div className="flex flex-wrap items-center justify-end gap-2">
            {/* Horizon: preset Select (12/18/24/36) + a custom 3–60 number input */}
            <div className="flex items-center gap-1.5">
              <span className="text-[11px] text-muted-foreground">Horizon</span>
              <Select
                value={HORIZON_PRESETS.includes(N) ? String(N) : 'custom'}
                onValueChange={(v) => { if (v !== 'custom') setMonths(v); }}
              >
                <SelectTrigger className="h-7 w-[116px] min-w-0 text-[11px]"><SelectValue className="block truncate" /></SelectTrigger>
                <SelectContent>
                  {HORIZON_PRESETS.map((h) => (
                    <SelectItem key={h} value={String(h)} className="text-[11px]">{h} months</SelectItem>
                  ))}
                  {!HORIZON_PRESETS.includes(N) && (
                    <SelectItem value="custom" className="text-[11px] text-muted-foreground">{N} months (custom)</SelectItem>
                  )}
                </SelectContent>
              </Select>
              <Input
                type="number" min="3" max="60" step={1}
                aria-label="Custom projection horizon (months)"
                className="h-7 w-14 tabular-nums text-[11px]"
                value={levers.months} onChange={set('months')}
              />
            </div>
            <ToggleGroup
              type="multiple"
              size="sm"
              variant="outline"
              value={series}
              onValueChange={setSeries}
              className="flex-wrap justify-end"
              aria-label="Chart series"
            >
              {SERIES_TOGGLES.map(([val, lbl]) => (
                <ToggleGroupItem key={val} value={val} className="h-7 px-2 text-[11px]" aria-label={lbl}>{lbl}</ToggleGroupItem>
              ))}
            </ToggleGroup>
          </div>
        )}
      >
        <ChartContainer
          config={{
            revenue: { label: 'Revenue', color: 'hsl(var(--chart-2))' },
            cogs: { label: 'COGS (AI + infra + data + hw)', color: 'hsl(var(--chart-3))' },
            opex: { label: 'Opex (staff + CAC)', color: 'hsl(var(--chart-4))' },
            ebitda: { label: 'EBITDA', color: 'hsl(var(--chart-1))' },
            aiTotal: { label: 'AI cost (total)', color: 'hsl(var(--chart-5))' },
            infra: { label: 'Infra', color: 'hsl(160 40% 45%)' },
            sttCost: { label: 'STT cost', color: 'hsl(199 45% 58%)' },
            ttsCost: { label: 'TTS cost', color: 'hsl(30 50% 55%)' },
          }}
          className="h-[290px] w-full"
        >
          <ComposedChart data={chartRows} margin={{ top: 6, right: 6, left: 0, bottom: 0 }}>
            <defs>
              <linearGradient id="fillRev" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="var(--color-revenue)" stopOpacity={0.3} />
                <stop offset="95%" stopColor="var(--color-revenue)" stopOpacity={0.03} />
              </linearGradient>
            </defs>
            <CartesianGrid vertical={false} strokeDasharray="3 3" />
            <XAxis dataKey="label" tickLine={false} axisLine={false} tickMargin={8} tick={AXIS_TICK} />
            <YAxis width={56} tickLine={false} axisLine={false} tick={AXIS_TICK} tickFormatter={(v) => fmtCompact(v)} />
            <ReferenceLine y={0} stroke="hsl(var(--muted-foreground))" strokeDasharray="4 4" />
            <ChartTooltip content={<ChartTooltipContent formatter={(v) => fmtINR(v)} />} />
            <ChartLegend content={<ChartLegendContent />} />
            {series.includes('revenue') && (
              <Area dataKey="revenue" type="monotone" stroke="var(--color-revenue)" strokeWidth={2} fill="url(#fillRev)" />
            )}
            {series.includes('cogs') && (
              <Line dataKey="cogs" type="monotone" stroke="var(--color-cogs)" strokeWidth={1.5} dot={false} />
            )}
            {series.includes('opex') && (
              <Line dataKey="opex" type="monotone" stroke="var(--color-opex)" strokeWidth={1.5} dot={false} />
            )}
            {series.includes('ebitda') && (
              <Line dataKey="ebitda" type="monotone" stroke="var(--color-ebitda)" strokeWidth={2.5} dot={false} />
            )}
            {series.includes('aiTotal') && (
              <Line dataKey="aiTotal" type="monotone" stroke="var(--color-aiTotal)" strokeWidth={1.5} dot={false} />
            )}
            {series.includes('infra') && (
              <Line dataKey="infra" type="monotone" stroke="var(--color-infra)" strokeWidth={1.5} strokeDasharray="4 3" dot={false} />
            )}
            {series.includes('sttCost') && (
              <Line dataKey="sttCost" type="monotone" stroke="var(--color-sttCost)" strokeWidth={1.5} strokeDasharray="4 3" dot={false} />
            )}
            {series.includes('ttsCost') && (
              <Line dataKey="ttsCost" type="monotone" stroke="var(--color-ttsCost)" strokeWidth={1.5} strokeDasharray="4 3" dot={false} />
            )}
          </ComposedChart>
        </ChartContainer>
      </ChartCard>

      {/* Per-hospital economics vs scale — answers "how do my per-hospital costs fall as
          I add hospitals?": variable serving cost stays flat, but the shaded fixed-cost
          share (staff + misc) and infra amortize across more hospitals, so total
          cost/hospital drops and margin/hospital expands. */}
      <ChartCard
        className="mt-3"
        title="Per-hospital economics at scale"
        subtitle={`₹/hospital/month as the installed base grows to ${scaleMax} · fixed cost (shaded) amortizes; variable serving cost stays flat`}
      >
        <ChartContainer
          config={{
            revenuePerHospital: { label: 'Revenue / hospital', color: 'hsl(var(--chart-2))' },
            totalCostPerHospital: { label: 'Total cost / hospital', color: 'hsl(var(--chart-3))' },
            marginPerHospital: { label: 'Margin / hospital', color: 'hsl(var(--chart-1))' },
            fixedShare: { label: 'Fixed-cost share', color: 'hsl(var(--chart-4))' },
          }}
          className="h-[260px] w-full"
        >
          <ComposedChart data={scaleRows} margin={{ top: 6, right: 6, left: 0, bottom: 0 }}>
            <defs>
              <linearGradient id="fillFixed" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="var(--color-fixedShare)" stopOpacity={0.28} />
                <stop offset="95%" stopColor="var(--color-fixedShare)" stopOpacity={0.03} />
              </linearGradient>
            </defs>
            <CartesianGrid vertical={false} strokeDasharray="3 3" />
            <XAxis dataKey="h" tickLine={false} axisLine={false} tickMargin={8} tick={AXIS_TICK}
              label={{ value: 'hospitals', position: 'insideBottomRight', offset: -2, fontSize: 10, fill: 'hsl(var(--muted-foreground))' }} />
            <YAxis width={56} tickLine={false} axisLine={false} tick={AXIS_TICK} tickFormatter={(v) => fmtCompact(v)} />
            <ReferenceLine y={0} stroke="hsl(var(--muted-foreground))" strokeDasharray="4 4" />
            <ChartTooltip content={<ChartTooltipContent formatter={(v) => fmtINR(v)} labelFormatter={(l) => `${l} hospitals`} />} />
            <ChartLegend content={<ChartLegendContent />} />
            <Area dataKey="fixedShare" type="monotone" stroke="var(--color-fixedShare)" strokeWidth={1} fill="url(#fillFixed)" />
            <Line dataKey="revenuePerHospital" type="monotone" stroke="var(--color-revenuePerHospital)" strokeWidth={2} dot={false} />
            <Line dataKey="totalCostPerHospital" type="monotone" stroke="var(--color-totalCostPerHospital)" strokeWidth={1.5} dot={false} />
            <Line dataKey="marginPerHospital" type="monotone" stroke="var(--color-marginPerHospital)" strokeWidth={2.5} dot={false} />
          </ComposedChart>
        </ChartContainer>
      </ChartCard>

      {/* Levers — full width, DIRECTLY below the chart, packed in columns */}
      <Card className="mt-3 gap-0 py-0">
        <CardHeader className="border-b py-4">
          <CardTitle className="text-[13.5px]">Levers</CardTitle>
          <CardDescription className="text-[11.5px]">Change any field — the projection above recomputes instantly · hover the ⓘ for what each means</CardDescription>
        </CardHeader>
        <CardContent className="columns-1 gap-8 p-4 md:columns-2 xl:columns-3 2xl:columns-4">
            {/* Growth */}
            <div className="mb-6 break-inside-avoid">
              <div className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">Growth</div>
              <div className="grid grid-cols-2 gap-3">
                <LeverField k="startingHospitals" label="Starting hospitals" step={1} levers={levers} onChange={set} chip={chipFor('startingHospitals')} />
                <LeverField k="hospitalsAddedPerMonth" label="Hospitals added / month" step={1} levers={levers} onChange={set} chip={chipFor('hospitalsAddedPerMonth')} />
                <LeverField k="churnMonthlyPct" label="Monthly churn %" step={0.5} levers={levers} onChange={set} chip={chipFor('churnMonthlyPct')} />
              </div>
            </div>

            {/* Subscription model */}
            <div className="mb-6 break-inside-avoid">
              <div className="mb-2 flex items-center gap-1 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                Subscription model <InfoDot text={FIELD_INFO.pricingModel} />
              </div>
              <div className="mb-3">
                <Select value={levers.pricingModel} onValueChange={(v) => setField('pricingModel', v)}>
                  <SelectTrigger className="h-8 w-full text-[12.5px]"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="flat">Flat ₹ / hospital / mo</SelectItem>
                    <SelectItem value="per-seat">Per doctor seat / mo</SelectItem>
                    <SelectItem value="hybrid">Flat + per-consult (hybrid)</SelectItem>
                    <SelectItem value="tiered">Tiered slabs + base (recommended)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-2 gap-3">
                {(L.pricingModel === 'flat' || L.pricingModel === 'hybrid') && (
                  <LeverField k="subscriptionPerHospitalINR" label="Subscription / hospital / mo (₹)" step={500} levers={levers} onChange={set} chip={chipFor('subscriptionPerHospitalINR')} />
                )}
                {L.pricingModel === 'hybrid' && (
                  <LeverField k="pricePerConsultINR" label="Price / consult (₹)" step={0.5} levers={levers} onChange={set} chip={chipFor('pricePerConsultINR')} />
                )}
                {L.pricingModel === 'per-seat' && (
                  <>
                    <LeverField k="pricePerSeatINR" label="Price / seat / mo (₹)" step={100} levers={levers} onChange={set} chip={chipFor('pricePerSeatINR')} />
                    <LeverField k="doctorsPerHospital" label="Doctors / hospital" step={1} levers={levers} onChange={set} chip={chipFor('doctorsPerHospital')} />
                  </>
                )}
                {L.pricingModel === 'tiered' && (
                  <>
                    <LeverField k="tierBaseFeeINR" label="Base fee / hospital / mo (₹)" step={500} levers={levers} onChange={set} chip={chipFor('tierBaseFeeINR')} />
                    <LeverField k="setupFeePerHospitalINR" label="Setup fee / hospital (₹, one-time)" step={5000} levers={levers} onChange={set} chip={chipFor('setupFeePerHospitalINR')} />
                    <LeverField k="slab1UpToConsults" label="Slab 1 up to (consults)" step={100} levers={levers} onChange={set} chip={chipFor('slab1UpToConsults')} />
                    <LeverField k="slab1INR" label="Slab 1 ₹ / consult" step={1} levers={levers} onChange={set} chip={chipFor('slab1INR')} />
                    <LeverField k="slab2UpToConsults" label="Slab 2 up to (consults)" step={100} levers={levers} onChange={set} chip={chipFor('slab2UpToConsults')} />
                    <LeverField k="slab2INR" label="Slab 2 ₹ / consult" step={1} levers={levers} onChange={set} chip={chipFor('slab2INR')} />
                    <LeverField k="slab3INR" label="Beyond ₹ / consult (slab 3)" step={1} levers={levers} onChange={set} chip={chipFor('slab3INR')} />
                  </>
                )}
              </div>
              {L.pricingModel === 'tiered' && (
                <div className="mt-3 rounded-lg bg-muted/60 px-3 py-2 text-[11.5px] leading-relaxed text-muted-foreground">
                  A typical hospital ({fmtNum(opdConsultsPerHospital)} consults/mo) pays
                  <span className="font-semibold text-foreground tabular-nums"> {fmtINR(tieredMonthlyRevenuePerHospital(opdConsultsPerHospital, L))}/mo</span>
                  <span className="text-muted-foreground/80"> = ₹{L.tierBaseFeeINR} base + graduated slabs</span>, plus a one-time
                  <span className="font-semibold text-foreground tabular-nums"> {fmtINR(L.setupFeePerHospitalINR)}</span> setup at signup.
                </div>
              )}
            </div>

            {/* OPD usage */}
            <div className="mb-6 break-inside-avoid">
              <div className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">OPD usage</div>
              <div className="grid grid-cols-2 gap-3">
                <LeverField k="consultsPerHospitalPerDay" label="Consults / hospital / day" step={5} levers={levers} onChange={set} chip={chipFor('consultsPerHospitalPerDay')} />
                <LeverField k="workingDaysPerMonth" label="Working days / month" step={1} levers={levers} onChange={set} chip={chipFor('workingDaysPerMonth')} />
              </div>
            </div>

            {/* AI cost per consult — derived breakdown + presets + override */}
            <div className="mb-6 break-inside-avoid">
              <div className="mb-2 flex items-center gap-1 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                AI cost / consult (derived) <InfoDot text="Blended AI ₹/consult feeding the P&L = STT minutes × ₹/min + TTS + LLM + messaging. Turn on the override to force a single number instead." />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <LeverField k="sttMinutesPerConsult" label="STT minutes / consult" step={0.5} levers={levers} onChange={set} disabled={overrideOn} chip={chipFor('sttMinutesPerConsult')} />
                <LeverField k="sttPerMinuteINR" label="STT ₹ / minute" step={0.05} levers={levers} onChange={set} disabled={overrideOn} chip={chipFor('sttPerMinuteINR')} />
                <LeverField k="ttsPerConsultINR" label="TTS ₹ / consult" step={0.5} levers={levers} onChange={set} disabled={overrideOn} chip={chipFor('ttsPerConsultINR')} />
                <LeverField k="llmPerConsultINR" label="LLM ₹ / consult" step={0.1} levers={levers} onChange={set} disabled={overrideOn} chip={chipFor('llmPerConsultINR')} />
                <LeverField k="messagingPerPatientINR" label="Messaging ₹ / patient" step={0.1} levers={levers} onChange={set} disabled={overrideOn} chip={chipFor('messagingPerPatientINR')} />
              </div>
              {/* Provider presets — fill the STT/TTS/LLM number from a named list rate */}
              <div className="mt-3 flex flex-col gap-2 rounded-lg bg-muted/40 px-3 py-2.5">
                <div className="text-[10.5px] font-semibold uppercase tracking-wide text-muted-foreground">Provider presets (list rates)</div>
                <PresetRow label="STT ₹ / min" k="sttPerMinuteINR" presets={STT_PRESETS} levers={levers} setField={setField} disabled={overrideOn} />
                <PresetRow label="TTS ₹ / consult" k="ttsPerConsultINR" presets={TTS_PRESETS} levers={levers} setField={setField} disabled={overrideOn} />
                <PresetRow label="LLM ₹ / consult" k="llmPerConsultINR" presets={LLM_PRESETS} levers={levers} setField={setField} disabled={overrideOn} />
              </div>
              <div className={`mt-3 rounded-lg px-3 py-2 text-[11.5px] leading-relaxed ${overrideOn ? 'bg-muted/40 text-muted-foreground' : 'bg-muted/60 text-muted-foreground'}`}>
                Blended <span className="font-semibold text-foreground tabular-nums">{fmtINR(aiParts.derivedTotal)}</span> / consult
                <span className="text-muted-foreground/80"> = STT {fmtINR(aiParts.stt)} + TTS {fmtINR(aiParts.tts)} + LLM {fmtINR(aiParts.llm)} + msg {fmtINR(aiParts.messaging)}</span>
                {overrideOn && <span className="text-[hsl(var(--ai-foreground))]"> · overridden</span>}
              </div>
              <div className="mt-3 flex items-center gap-2">
                <Switch id="ai-override" checked={overrideOn} onCheckedChange={(v) => setField('aiCostOverrideEnabled', v)} />
                <Label htmlFor="ai-override" className="text-[11.5px] text-muted-foreground">Use blended override</Label>
                <div className="ml-auto flex items-center gap-1">
                  <span className="text-[11px] text-muted-foreground">₹</span>
                  <Input type="number" step={0.5} min="0" aria-label="Blended override ₹ per consult"
                    disabled={!overrideOn}
                    className="h-8 w-20 tabular-nums text-[12.5px]"
                    value={levers.aiCostOverrideINR} onChange={set('aiCostOverrideINR')} />
                  <InfoDot text={FIELD_INFO.aiCostOverrideINR} />
                </div>
              </div>
              {chipFor('aiCostOverrideINR')}
            </div>

            {/* IPD module */}
            <div className="mb-6 break-inside-avoid">
              <div className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">IPD (in-patient)</div>
              <div className="mb-3 flex items-center gap-2">
                <Label className="flex items-center gap-1 text-[11px] leading-tight text-muted-foreground">
                  Charge basis <InfoDot text={FIELD_INFO.ipdPricingMode} />
                </Label>
                <Select value={levers.ipdPricingMode} onValueChange={(v) => setField('ipdPricingMode', v)}>
                  <SelectTrigger className="ml-auto h-8 w-40 text-[12.5px]"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="per-admission">Per admission</SelectItem>
                    <SelectItem value="per-bed-day">Per bed-day</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <LeverField k="ipdHospitalPct" label="% hospitals with IPD" step={5} levers={levers} onChange={set} chip={chipFor('ipdHospitalPct')} />
                <LeverField k="ipdAdmissionsPerHospitalPerMonth" label="Admissions / hospital / mo" step={5} levers={levers} onChange={set} chip={chipFor('ipdAdmissionsPerHospitalPerMonth')} />
                <LeverField k="ipdAvgLengthOfStayDays" label="Avg length of stay (days)" step={0.5} levers={levers} onChange={set} chip={chipFor('ipdAvgLengthOfStayDays')} />
                {L.ipdPricingMode === 'per-bed-day'
                  ? <LeverField k="ipdPricePerBedDayINR" label="Price / bed-day (₹)" step={10} levers={levers} onChange={set} chip={chipFor('ipdPricePerBedDayINR')} />
                  : <LeverField k="ipdPricePerAdmissionINR" label="Price / admission (₹)" step={25} levers={levers} onChange={set} chip={chipFor('ipdPricePerAdmissionINR')} />}
                <LeverField k="ipdAiCostPerBedDayINR" label="AI cost / bed-day (₹)" step={0.5} levers={levers} onChange={set} chip={chipFor('ipdAiCostPerBedDayINR')} />
              </div>
            </div>

            {/* Acquisition, hardware & team */}
            <div className="mb-6 break-inside-avoid">
              <div className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">Acquisition &amp; team</div>
              <div className="grid grid-cols-2 gap-3">
                <LeverField k="cacPerHospitalINR" label="CAC per hospital (₹)" step={5000} levers={levers} onChange={set} chip={chipFor('cacPerHospitalINR')} />
                <LeverField k="kioskCostINR" label="Per-kiosk cost to us (₹)" step={2500} levers={levers} onChange={set} chip={chipFor('kioskCostINR')} />
                <LeverField k="kioskSellPriceINR" label="Kiosk sell price (₹, 0 = free)" step={5000} levers={levers} onChange={set} chip={chipFor('kioskSellPriceINR')} />
                <LeverField k="kiosksPerHospital" label="Kiosks per hospital" step={1} levers={levers} onChange={set} chip={chipFor('kiosksPerHospital')} />
                <LeverField k="hardwareAmortizationMonths" label="Hardware amortization (months)" step={6} levers={levers} onChange={set} disabled={hwSold} chip={chipFor('hardwareAmortizationMonths')} />
                <LeverField k="mobileDataPerSeatINR" label="Mobile data / seat / mo (₹)" step={50} levers={levers} onChange={set} chip={chipFor('mobileDataPerSeatINR')} />
                <LeverField k="staffCostMonthlyINR" label="Staff cost / month (₹)" step={10000} levers={levers} onChange={set} chip={chipFor('staffCostMonthlyINR')} />
                <LeverField k="miscOpexMonthlyINR" label="Misc opex / month (₹)" step={5000} levers={levers} onChange={set} chip={chipFor('miscOpexMonthlyINR')} />
              </div>
            </div>

            {/* Finance */}
            <div className="mb-6 break-inside-avoid">
              <div className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">Finance (NPV / IRR)</div>
              <div className="grid grid-cols-2 gap-3">
                <LeverField k="discountRateAnnualPct" label="Discount rate % / yr" step={1} levers={levers} onChange={set} chip={chipFor('discountRateAnnualPct')} />
              </div>
            </div>

            {/* Infra step */}
            <div className="mb-6 break-inside-avoid">
              <div className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">Infra — tiered &ldquo;go server&rdquo; step</div>
              <div className="grid grid-cols-2 gap-3">
                <LeverField k="baseInfraMonthlyINR" label="Shared baseline / month (₹)" step={2000} levers={levers} onChange={set} chip={chipFor('baseInfraMonthlyINR')} />
                <LeverField k="hospitalsPerServer" label="Hospitals per server" step={1} levers={levers} onChange={set} chip={chipFor('hospitalsPerServer')} />
                <LeverField k="serverStepMonthlyINR" label="Each extra server / month (₹)" step={2000} levers={levers} onChange={set} chip={chipFor('serverStepMonthlyINR')} />
              </div>
            </div>

            <div className="break-inside-avoid rounded-lg bg-muted/60 px-3 py-2 text-[11.5px] leading-relaxed text-muted-foreground">
              Infra now: <span className="font-medium text-foreground tabular-nums">{fmtINR(infraCostForHospitals(L.startingHospitals, L))}/mo</span> at {L.startingHospitals} hospitals →
              <span className="font-medium text-foreground tabular-nums"> {fmtINR(infraCostForHospitals(Math.round(summary.endingHospitals), L))}/mo</span> at {summary.endingHospitals.toFixed(0)}. {hwSold
                ? <>Hardware <span className="font-medium text-foreground">sold</span> at <span className="font-medium text-foreground tabular-nums">{fmtINR(L.kioskSellPriceINR * L.kiosksPerHospital)}</span>/hospital (cost {fmtINR(hardwarePerHospital(L))} → <span className="font-medium text-foreground tabular-nums">{fmtINR(hwMarginPerHospital)}</span> margin, booked at signup).</>
                : <>Hardware <span className="font-medium text-foreground">given free</span> per hospital: <span className="font-medium text-foreground tabular-nums">{fmtINR(hardwarePerHospital(L))}</span> capex, amortized {L.hardwareAmortizationMonths} mo.</>} Mobile data <span className="font-medium text-foreground tabular-nums">{fmtINR(mobileDataPerHospital(L))}</span>/hospital/mo.
            </div>
          </CardContent>
        </Card>

      {/* Infra step + P&L — below the levers */}
      <div className="mt-3 grid grid-cols-1 gap-3 xl:grid-cols-3">
          {/* Infra step function */}
          <ChartCard title='"Go server" infra step function' subtitle={`₹${fmtNum(L.baseInfraMonthlyINR)} base + one ₹${fmtNum(L.serverStepMonthlyINR)} server per ${L.hospitalsPerServer} hospitals`}>
            <ChartContainer config={{ infra: { label: 'Infra ₹/mo', color: 'hsl(var(--chart-5))' } }} className="h-[180px] w-full">
              <BarChart data={stepRows} margin={{ top: 6, right: 6, left: 0, bottom: 0 }}>
                <CartesianGrid vertical={false} strokeDasharray="3 3" />
                <XAxis dataKey="h" tickLine={false} axisLine={false} tickMargin={8} tick={AXIS_TICK}
                  label={{ value: 'hospitals', position: 'insideBottomRight', offset: -2, fontSize: 10, fill: 'hsl(var(--muted-foreground))' }} />
                <YAxis width={56} tickLine={false} axisLine={false} tick={AXIS_TICK} tickFormatter={(v) => fmtCompact(v)} />
                <ChartTooltip cursor={false} content={<ChartTooltipContent formatter={(v) => fmtINR(v)} labelFormatter={(l) => `${l} hospitals`} />} />
                <Bar dataKey="infra" fill="var(--color-infra)" radius={[2, 2, 0, 0]} />
              </BarChart>
            </ChartContainer>
          </ChartCard>

          {/* P&L table — horizontal scroll kept */}
          <Card className="gap-0 overflow-hidden py-0 xl:col-span-2">
            <CardHeader className="border-b py-4">
              <CardTitle className="text-[13.5px]">Monthly P&amp;L (EBITDA view)</CardTitle>
              <CardDescription className="text-[11.5px]">All INR · revenue split OPD vs IPD · acquisition cost expensed in the month incurred</CardDescription>
            </CardHeader>
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="pl-4">Month</TableHead>
                      <TableHead className="text-right">Hospitals</TableHead>
                      <TableHead className="text-right">Consults</TableHead>
                      <TableHead className="text-right">Revenue</TableHead>
                      <TableHead className="text-right">Rev OPD</TableHead>
                      <TableHead className="text-right">Rev IPD</TableHead>
                      <TableHead className="text-right">AI cost</TableHead>
                      <TableHead className="text-right">Infra</TableHead>
                      <TableHead className="text-right">Gross margin</TableHead>
                      <TableHead className="text-right">Opex</TableHead>
                      <TableHead className="text-right">EBITDA</TableHead>
                      <TableHead className="text-right">Capex</TableHead>
                      <TableHead className="text-right">Cash flow</TableHead>
                      <TableHead className="text-right">Hardware amort.</TableHead>
                      <TableHead className="text-right">≈EBIT</TableHead>
                      <TableHead className="pr-4 text-right">Cumulative</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {months.map((m) => (
                      <TableRow key={m.month}>
                        <TableCell className="pl-4 font-medium text-foreground">M{m.month}</TableCell>
                        <TableCell className="text-right tabular-nums">{m.hospitals.toFixed(1)}</TableCell>
                        <TableCell className="text-right tabular-nums">{fmtCompact(m.consults)}</TableCell>
                        <TableCell className="text-right tabular-nums">{fmtINR(Math.round(m.revenue))}</TableCell>
                        <TableCell className="text-right tabular-nums text-muted-foreground">{fmtINR(Math.round(m.revenueOPD))}</TableCell>
                        <TableCell className="text-right tabular-nums text-muted-foreground">{fmtINR(Math.round(m.revenueIPD))}</TableCell>
                        <TableCell className="text-right tabular-nums text-muted-foreground">{fmtINR(Math.round(m.aiCost))}</TableCell>
                        <TableCell className="text-right tabular-nums text-muted-foreground">{fmtINR(Math.round(m.infra))}</TableCell>
                        <TableCell className="text-right tabular-nums">{fmtINR(Math.round(m.grossMargin))}</TableCell>
                        <TableCell className="text-right tabular-nums text-muted-foreground">{fmtINR(Math.round(m.opex))}</TableCell>
                        <TableCell className={`text-right tabular-nums font-semibold ${m.ebitda >= 0 ? 'text-[hsl(var(--success))]' : 'text-[hsl(var(--allergy))]'}`}>
                          {fmtINR(Math.round(m.ebitda))}
                        </TableCell>
                        <TableCell className="text-right tabular-nums text-muted-foreground">{fmtINR(Math.round(m.capex))}</TableCell>
                        <TableCell className={`text-right tabular-nums ${m.cashFlow >= 0 ? 'text-[hsl(var(--success))]' : 'text-[hsl(var(--allergy))]'}`}>
                          {fmtINR(Math.round(m.cashFlow))}
                        </TableCell>
                        <TableCell className="text-right tabular-nums text-muted-foreground">{fmtINR(Math.round(m.hardwareAmortization))}</TableCell>
                        <TableCell className={`text-right tabular-nums ${m.ebitAfterHardware >= 0 ? 'text-[hsl(var(--success))]' : 'text-[hsl(var(--allergy))]'}`}>
                          {fmtINR(Math.round(m.ebitAfterHardware))}
                        </TableCell>
                        <TableCell className={`pr-4 text-right tabular-nums ${m.cumulativeEbitda >= 0 ? 'text-[hsl(var(--success))]' : 'text-muted-foreground'}`}>
                          {fmtINR(Math.round(m.cumulativeEbitda))}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>
      </div>

      {/* Assumptions footnote — the standing answer to "how did we get these numbers" */}
      <Card className="mt-4 gap-0 py-0">
        <CardHeader className="border-b py-4">
          <CardTitle className="text-[13.5px]">Assumptions &amp; sources</CardTitle>
          <CardDescription className="text-[11.5px]">Every seeded default and where it came from — replace the &ldquo;assumption&rdquo; rows with observed numbers as they land.</CardDescription>
        </CardHeader>
        <CardContent className="grid grid-cols-1 gap-x-6 gap-y-1.5 p-4 sm:grid-cols-2">
          {ASSUMPTIONS.map(([text, status]) => {
            const meta = STATUS_META[status];
            return (
              <div key={text} className="flex items-center justify-between gap-2 text-[11.5px]">
                <span className="min-w-0 text-muted-foreground">{text}</span>
                <span className={`flex-none rounded border px-1.5 py-0.5 text-[10px] font-semibold ${meta.cls}`}>{meta.label}</span>
              </div>
            );
          })}
        </CardContent>
      </Card>
    </div>
  );
}
